import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause, Volume2, VolumeX } from 'lucide-react';
import { motion } from 'framer-motion';

export const AudioPlayer = ({ audioUrl, title, artist }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.7);
  const [isMuted, setIsMuted] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const setAudioData = () => {
      setDuration(audio.duration);
    };

    const setAudioTime = () => {
      setCurrentTime(audio.currentTime);
    };

    audio.addEventListener('loadeddata', setAudioData);
    audio.addEventListener('timeupdate', setAudioTime);
    audio.addEventListener('ended', () => setIsPlaying(false));

    return () => {
      audio.removeEventListener('loadeddata', setAudioData);
      audio.removeEventListener('timeupdate', setAudioTime);
      audio.removeEventListener('ended', () => setIsPlaying(false));
    };
  }, []);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = isMuted ? 0 : volume;
    }
  }, [volume, isMuted]);

  const togglePlay = () => {
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleProgressChange = (e) => {
    const newTime = (e.target.value / 100) * duration;
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const handleVolumeChange = (e) => {
    const newVolume = e.target.value / 100;
    setVolume(newVolume);
    if (newVolume > 0) setIsMuted(false);
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
  };

  const formatTime = (time) => {
    if (isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const progress = duration ? (currentTime / duration) * 100 : 0;

  return (
    <div className="relative" data-testid="audio-player">
      <audio ref={audioRef} src={audioUrl} preload="metadata" />
      
      <div className="bg-gradient-to-r from-[#2C221F] to-[#1A1412] rounded-2xl p-8 border border-[#D97736]/30 shadow-[0_20px_60px_-15px_rgba(217,119,54,0.3)]">
        <div className="flex items-center gap-6">
          {/* Vinyl Record Animation */}
          <motion.div
            animate={{ rotate: isPlaying ? 360 : 0 }}
            transition={{ duration: 3, repeat: isPlaying ? Infinity : 0, ease: "linear" }}
            className="flex-shrink-0"
          >
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#1A1412] to-[#2C221F] border-4 border-[#D97736] flex items-center justify-center relative">
              <div className="w-8 h-8 rounded-full bg-[#CFB53B]"></div>
              <div className="absolute inset-0 rounded-full" style={{
                background: 'repeating-conic-gradient(from 0deg, transparent 0deg 10deg, rgba(217, 119, 54, 0.1) 10deg 20deg)'
              }}></div>
            </div>
          </motion.div>

          {/* Player Controls */}
          <div className="flex-1">
            {/* Title */}
            <div className="mb-4">
              <h3 className="font-['Playfair_Display'] text-2xl font-bold text-[#FDFBF7] mb-1">
                {title || 'Featured Track'}
              </h3>
              <p className="text-[#CFB53B] text-sm font-['Outfit']">{artist || 'Vintage Ave Records'}</p>
            </div>

            {/* Progress Bar */}
            <div className="mb-4">
              <input
                type="range"
                min="0"
                max="100"
                value={progress}
                onChange={handleProgressChange}
                className="w-full h-2 bg-[#3D302C] rounded-full appearance-none cursor-pointer"
                style={{
                  background: `linear-gradient(to right, #D97736 0%, #D97736 ${progress}%, #3D302C ${progress}%, #3D302C 100%)`
                }}
                data-testid="progress-bar"
              />
              <div className="flex justify-between text-xs text-[#A89B95] mt-1 font-['Outfit']">
                <span>{formatTime(currentTime)}</span>
                <span>{formatTime(duration)}</span>
              </div>
            </div>

            {/* Controls Row */}
            <div className="flex items-center gap-4">
              {/* Play/Pause Button */}
              <button
                onClick={togglePlay}
                className="w-12 h-12 rounded-full bg-[#D97736] hover:bg-[#F08F4D] flex items-center justify-center transition-colors shadow-lg"
                data-testid="play-pause-button"
              >
                {isPlaying ? (
                  <Pause className="text-white" size={24} fill="white" />
                ) : (
                  <Play className="text-white ml-1" size={24} fill="white" />
                )}
              </button>

              {/* Volume Control */}
              <div className="flex items-center gap-2 flex-1">
                <button
                  onClick={toggleMute}
                  className="text-[#D97736] hover:text-[#F08F4D] transition-colors"
                  data-testid="mute-button"
                >
                  {isMuted || volume === 0 ? (
                    <VolumeX size={20} />
                  ) : (
                    <Volume2 size={20} />
                  )}
                </button>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={isMuted ? 0 : volume * 100}
                  onChange={handleVolumeChange}
                  className="w-24 h-1 bg-[#3D302C] rounded-full appearance-none cursor-pointer"
                  style={{
                    background: `linear-gradient(to right, #D97736 0%, #D97736 ${isMuted ? 0 : volume * 100}%, #3D302C ${isMuted ? 0 : volume * 100}%, #3D302C 100%)`
                  }}
                  data-testid="volume-slider"
                />
              </div>

              {/* Waveform Visualization */}
              <div className="hidden md:flex items-center gap-1 ml-auto">
                {[...Array(8)].map((_, i) => (
                  <motion.div
                    key={i}
                    animate={{
                      scaleY: isPlaying ? [0.3, 1, 0.3] : 0.3
                    }}
                    transition={{
                      duration: 0.6,
                      repeat: isPlaying ? Infinity : 0,
                      delay: i * 0.1
                    }}
                    className="w-1 h-8 bg-[#CFB53B] rounded-full origin-center"
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};